/**
 * Every error as a row, newest first (19/10/2026), with the column heads;
 * the first 50 show, the rest on demand. 21/10/2026 — within each day the
 * open ones first, the resolved ones under them (each newest first): what
 * still needs a person stays on top of its day.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import type { DropGenErrorRow, DropGenErrorsReport } from '@trafficpulse/shared';

import { SectionAction } from '../../timeline/section/SectionAction';
import { ErrorRow, GRID } from './ErrorRow';

const SHOWN = 50;

const dayKey = (r: DropGenErrorRow) => r.runDate ?? r.at.slice(0, 10);

/** Within each day, open first, then resolved — the server's order (newest first) kept inside each, the days newest first (a just-resolved error never sinks under older days' rows). */
export const openFirst = (rows: DropGenErrorRow[]): DropGenErrorRow[] =>
  rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => dayKey(b.r).localeCompare(dayKey(a.r)) || Number(Boolean(a.r.resolvedAt)) - Number(Boolean(b.r.resolvedAt)) || a.i - b.i)
    .map((x) => x.r);

export const ErrorsTable: FC<{ rows: DropGenErrorRow[]; report: Pick<DropGenErrorsReport, 'to' | 'countryKey'>; days: number; onEditContainer?: (containerId: string) => void }> = ({ rows, report, days, onEditContainer }) => {
  const [all, setAll] = useState(false);
  const sorted = openFirst(rows);
  return (
    <div className="border-t border-border/40" data-testid="errors-table">
      <div className="overflow-x-auto">
        <div className="min-w-[1020px]">
          <div className={`${GRID} py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground`}>
            <span>When</span><span>Kind</span><span>Where</span><span>What happened · why</span><span>Slack</span><span>Status</span>
          </div>
          {rows.length === 0 ? <p className="px-5 py-4 text-xs text-muted-foreground">No error of these kinds.</p> : null}
          {(all ? sorted : sorted.slice(0, SHOWN)).map((r) => (
            <ErrorRow key={r.id} row={r} showDate={days > 1} countryKey={report.countryKey} onEditContainer={onEditContainer} />
          ))}
        </div>
      </div>
      {rows.length > SHOWN ? (
        <div className="px-5 py-2">
          <SectionAction quiet onClick={() => setAll((v) => !v)}>
            {all ? `Show the first ${SHOWN}` : `Show all ${rows.length} errors`}
          </SectionAction>
        </div>
      ) : null}
    </div>
  );
};
